<template>
  <div class="min-h-screen bg-gray-50">
    <!-- Header -->
    <header class="bg-white shadow-sm">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="flex justify-between items-center h-16">
          <div class="flex items-center">
            <img class="h-8 w-auto" src="/logo-mifo.svg" alt="MIFO" />
          </div>
        </div>
      </div>
    </header>

    <div class="max-w-4xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
      <div v-if="isLoading" class="text-center">
        <!-- Loading State -->
        <div class="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 mb-4">
          <svg class="animate-spin h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        </div>
        
        <h1 class="text-3xl font-heading font-bold text-blue-600 mb-4">
          Zahlung wird überprüft...
        </h1>
        
        <p class="text-lg text-gray-600 mb-8">
          Bitte warten Sie, während wir den Status Ihrer Zahlung überprüfen.
        </p>
      </div>
      
      <div v-else class="text-center">
        <!-- Status Icon -->
        <div v-if="order?.status === 'paid'" class="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
          <svg class="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
          </svg>
        </div>
        <div v-else-if="order?.status === 'failed'" class="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
          <svg class="h-6 w-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
          </svg>
        </div>
        <div v-else-if="order?.status === 'pending_email'" class="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 mb-4">
          <svg class="h-6 w-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path>
          </svg>
        </div>
        <div v-else-if="order?.status === 'expired'" class="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-gray-100 mb-4">
          <svg class="h-6 w-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
          </svg>
        </div>
        <div v-else class="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-yellow-100 mb-4">
          <svg class="h-6 w-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
          </svg>
        </div>

        <h1 v-if="order?.status === 'paid'" class="text-3xl font-heading font-bold text-green-600 mb-4">
          Zahlung erfolgreich!
        </h1>
        <h1 v-else-if="order?.status === 'failed'" class="text-3xl font-heading font-bold text-red-600 mb-4">
          Zahlung fehlgeschlagen
        </h1>
        <h1 v-else-if="order?.status === 'open'" class="text-3xl font-heading font-bold text-orange-600 mb-4">
          Zahlung offen
        </h1>
        <h1 v-else-if="order?.status === 'pending_email'" class="text-3xl font-heading font-bold text-blue-600 mb-4">
          Bestätigung per E-Mail
        </h1>
        <h1 v-else-if="order?.status === 'expired'" class="text-3xl font-heading font-bold text-gray-600 mb-4">
          Zahlung abgelaufen
        </h1>
        <h1 v-else class="text-3xl font-heading font-bold text-gray-600 mb-4">
          Zahlung wird überprüft
        </h1>
        
        <p v-if="order?.status === 'paid'" class="text-lg text-gray-600 mb-8">
          Vielen Dank für Ihre Buchung. Sie erhalten in Kürze eine Bestätigungs-E-Mail mit allen Details.
        </p>
        <p v-else-if="order?.status === 'failed'" class="text-lg text-gray-600 mb-8">
          Die Zahlung konnte nicht abgeschlossen werden. Bitte versuchen Sie es erneut oder kontaktieren Sie uns.
        </p>
        <p v-else-if="order?.status === 'open'" class="text-lg text-gray-600 mb-8">
          Ihre Zahlung ist noch nicht abgeschlossen. Bitte vervollständigen Sie den Zahlungsvorgang, um Ihre Buchung zu bestätigen.
        </p>
        <p v-else-if="order?.status === 'pending_email'" class="text-lg text-gray-600 mb-8">
          Wir konnten den Status Ihrer Zahlung nicht automatisch bestätigen. Sie erhalten eine E-Mail-Bestätigung, sobald wir ein Update erhalten. Bitte überprüfen Sie auch Ihren Spam-Ordner.
        </p>
        <p v-else-if="order?.status === 'expired'" class="text-lg text-gray-600 mb-8">
          Ihre Zahlung ist abgelaufen. Bitte versuchen Sie eine neue Buchung oder kontaktieren Sie uns.
        </p>
        <p v-else class="text-lg text-gray-600 mb-8">
          Der Status Ihrer Zahlung wird überprüft...
        </p>

        <!-- Order Details -->
        <div v-if="order" class="card max-w-2xl mx-auto mb-8">
          <h2 class="text-xl font-heading font-semibold text-mifo-primary mb-4">
            Ihre Buchung
          </h2>
          
          <div class="space-y-4 text-left">
            <div class="flex justify-between">
              <span class="text-gray-600">Buchungsnummer:</span>
              <span class="font-medium">{{ order.id }}</span>
            </div>
            
            <div class="flex justify-between">
              <span class="text-gray-600">Kunde:</span>
              <span class="font-medium">{{ order.customerName || 'N/A' }}</span>
            </div>
            
            <div class="flex justify-between">
              <span class="text-gray-600">Kurs:</span>
              <span class="font-medium">{{ order.courseTitle || 'Echokardiographie Grundkurs' }}</span>
            </div>
            
            <div class="flex justify-between">
              <span class="text-gray-600">Betrag:</span>
              <span class="font-medium">{{ formatCurrency(order.totalAmount) }}</span>
            </div>
            
            <div class="flex justify-between">
              <span class="text-gray-600">Mollie Zahlungs-ID:</span>
              <span class="font-medium font-mono text-sm">{{ order.paymentId || 'N/A' }}</span>
            </div>
            
            <div class="flex justify-between">
              <span class="text-gray-600">Status:</span>
              <span v-if="order.status === 'paid'" class="font-medium text-green-600">Bezahlt</span>
              <span v-else-if="order.status === 'failed'" class="font-medium text-red-600">Fehlgeschlagen</span>
              <span v-else-if="order.status === 'pending'" class="font-medium text-yellow-600">Ausstehend</span>
              <span v-else-if="order.status === 'open'" class="font-medium text-orange-600">Offen</span>
              <span v-else-if="order.status === 'expired'" class="font-medium text-gray-600">Abgelaufen</span>
              <span v-else-if="order.status === 'pending_email'" class="font-medium text-blue-600">E-Mail-Bestätigung</span>
              <span v-else class="font-medium text-gray-600">{{ order.status }}</span>
            </div>
          </div>
        </div>

        <!-- Action Buttons -->
        <div class="space-y-4">
          <button
            @click="goToHome"
            class="btn-primary"
          >
            Zurück zur Startseite
          </button>
          
          <div class="text-sm text-gray-500">
            Bei Fragen kontaktieren Sie uns unter: 
            <a href="mailto:info@mifokurse.de" class="text-mifo-accent hover:underline">
              info@mifokurse.de
            </a>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, onUnmounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { formatCurrency } from 'shared/utils'
import axios from 'axios'

export default {
  name: 'CheckoutSuccess',
  setup() {
    const route = useRoute()
    const router = useRouter()
    const order = ref(null)
    const isLoading = ref(true)
    const pollingInterval = ref(null)
    const pollingAttempts = ref(0)
    const maxPollingAttempts = 5

    const goToHome = () => {
      router.push('/')
    }

    const checkPaymentStatus = async (paymentId) => {
      try {
        const paymentResponse = await axios.get(`/api/orders/payment-status/${paymentId}`)
        const paymentStatus = paymentResponse.data
        
        return paymentStatus
      } catch (error) {
        console.error('Error checking payment status:', error)
        // Return a mock status for development if API fails
        return {
          id: paymentId,
          status: 'unknown',
          amount: { currency: 'EUR', value: '0.00' },
          description: 'Payment status unavailable'
        }
      }
    }

    const startPolling = (paymentId) => {
      pollingAttempts.value = 0
      
      // Check immediately
      checkPaymentStatus(paymentId).then(paymentStatus => {
        updateOrderStatus(paymentStatus)
        pollingAttempts.value++
      }).catch(() => {
        // If initial check fails, still start polling
        updateOrderStatus({ status: 'unknown' })
        pollingAttempts.value++
      })

      // Then poll every 5 seconds, maximum 5 times
      pollingInterval.value = setInterval(async () => {
        pollingAttempts.value++
        
        try {
          const paymentStatus = await checkPaymentStatus(paymentId)
          updateOrderStatus(paymentStatus)
          
          // Stop polling if payment is no longer pending/open or if we get an unknown status
          if (paymentStatus.status !== 'pending' && paymentStatus.status !== 'open' && paymentStatus.status !== 'unknown') {
            clearInterval(pollingInterval.value)
            pollingInterval.value = null
          } else if (pollingAttempts.value >= maxPollingAttempts) {
            clearInterval(pollingInterval.value)
            pollingInterval.value = null
            // Update status to indicate polling stopped
            order.value = {
              ...order.value,
              status: 'pending_email'
            }
          }
        } catch (error) {
          console.error('Error during polling:', error)
          // Continue polling even if there's an error, but count the attempt
          if (pollingAttempts.value >= maxPollingAttempts) {
            clearInterval(pollingInterval.value)
            pollingInterval.value = null
            // Update status to indicate polling stopped
            order.value = {
              ...order.value,
              status: 'pending_email'
            }
          }
        }
      }, 5000)
    }

    const updateOrderStatus = (paymentStatus) => {
      const orderId = route.query.orderId
      const customerName = route.query.customerName
      const amount = route.query.amount
      const paymentId = route.query.paymentId
      
      order.value = {
        id: orderId,
        customerName: customerName ? decodeURIComponent(customerName) : 'N/A',
        courseTitle: 'Echokardiographie Grundkurs',
        totalAmount: amount ? parseFloat(amount) * 100 : 170000,
        paymentId: paymentStatus.id || paymentId || 'N/A',
        status: paymentStatus.status || 'unknown'
      }
      
      isLoading.value = false
    }

    onMounted(async () => {
      const orderId = route.query.orderId
      const paymentId = route.query.paymentId
      const customerName = route.query.customerName
      const amount = route.query.amount
      
      if (orderId && paymentId) {
        // Start polling for payment status
        startPolling(paymentId)
      } else if (orderId) {
        // Try to get payment ID from order lookup
        try {
          const orderResponse = await axios.get(`/api/orders/${orderId}`)
          const orderData = orderResponse.data
          
          if (orderData.molliePaymentId) {
            startPolling(orderData.molliePaymentId)
          } else {
            // Fallback for when paymentId is not available
            order.value = {
              id: orderId,
              customerName: customerName ? decodeURIComponent(customerName) : 'N/A',
              courseTitle: 'Echokardiographie Grundkurs',
              totalAmount: amount ? parseFloat(amount) * 100 : 170000,
              paymentId: paymentId || 'N/A',
              status: 'unknown'
            }
            isLoading.value = false
          }
        } catch (error) {
          console.error('Error fetching order:', error)
          // Fallback for when paymentId is not available
          order.value = {
            id: orderId,
            customerName: customerName ? decodeURIComponent(customerName) : 'N/A',
            courseTitle: 'Echokardiographie Grundkurs',
            totalAmount: amount ? parseFloat(amount) * 100 : 170000,
            paymentId: paymentId || 'N/A',
            status: 'unknown'
          }
          isLoading.value = false
        }
      } else {
        isLoading.value = false
      }
    })

    // Cleanup polling interval on component unmount
    onUnmounted(() => {
      if (pollingInterval.value) {
        clearInterval(pollingInterval.value)
      }
    })

    return {
      order,
      isLoading,
      pollingAttempts,
      maxPollingAttempts,
      formatCurrency,
      goToHome
    }
  }
}
</script> 